// ==========================================================================
// lib/kitchen.ts — shopping list and recipe book (FF-051, 10 Oct 2026)
//   James: "lets go with Shopping list … have a option to have saved receipes
//   like a receipe book either from a website, screeen shot on a phone or a
//   custome text one we create with chat into a MD file."
//   - The list is shared; both phones refresh it every few seconds while open.
//   - A website recipe is read from the page's own recipe data (Worker, no AI).
//   - A photo or screenshot is read by the reader only when "Read it" is pressed.
//   - Markdown in and out: paste a recipe written with Claude, or copy one out.
// ==========================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { useHouseholdId, useMe } from "./household";
import { shrink, worker } from "./receipts";

export type Section = "fruit_veg" | "bakery" | "meat_seafood" | "dairy_eggs" | "pantry" | "frozen" | "drinks" | "baby" | "household" | "health_beauty" | "other";
/** In the order you walk the shop. */
export const SECTIONS: { key: Section; label: string }[] = [
  { key: "fruit_veg", label: "Fruit and veg" }, { key: "bakery", label: "Bakery" }, { key: "meat_seafood", label: "Meat and seafood" },
  { key: "dairy_eggs", label: "Dairy and eggs" }, { key: "pantry", label: "Pantry" }, { key: "frozen", label: "Frozen" },
  { key: "drinks", label: "Drinks" }, { key: "baby", label: "Baby" }, { key: "household", label: "Household" },
  { key: "health_beauty", label: "Health and beauty" }, { key: "other", label: "Other" },
];
export const sectionLabel = (s: Section) => SECTIONS.find((x) => x.key === s)?.label ?? "Other";

const WORDS: [Section, RegExp][] = [
  ["baby", /\b(nappies|nappy|wipes|formula|baby|bottle teats?|dummy|dummies|rusks?)\b/i],
  ["fruit_veg", /\b(apples?|bananas?|oranges?|lemons?|limes?|berries|strawberr|blueberr|grapes?|pears?|mango|avocados?|tomato|potato|sweet potato|onions?|garlic|ginger|carrots?|celery|lettuce|spinach|kale|broccoli|cauliflower|zucchini|capsicum|cucumber|mushrooms?|pumpkin|corn|peas|beans|herbs?|parsley|coriander|basil|mint|chilli|shallots?|leeks?|salad|veg|fruit)\b/i],
  ["bakery", /\b(bread|loaf|rolls?|wraps?|tortillas?|bagels?|croissants?|muffins?|buns?|sourdough|crumpets?|pita)\b/i],
  ["meat_seafood", /\b(mince|beef|steak|chicken|pork|lamb|bacon|ham|sausages?|salami|prosciutto|fish|salmon|prawns?|tuna steak|barramundi|chorizo|turkey|meat)\b/i],
  ["dairy_eggs", /\b(milk|cheese|cheddar|parmesan|mozzarella|feta|butter|yoghurt|yogurt|cream|eggs?|sour cream|custard)\b/i],
  ["frozen", /\b(frozen|ice cream|ice-cream|ice blocks?|frozen peas)\b/i],
  ["drinks", /\b(juice|soft drink|coke|soda|sparkling|water bottles?|coffee|tea bags?|tea|wine|beer|kombucha|cordial)\b/i],
  ["household", /\b(toilet paper|paper towels?|dishwashing|dishwasher|laundry|detergent|bin bags?|garbage bags?|cling ?wrap|foil|sponges?|cleaner|bleach|batteries|light ?bulbs?|tissues)\b/i],
  ["health_beauty", /\b(shampoo|conditioner|soap|body wash|toothpaste|toothbrush|deodorant|sunscreen|razors?|panadol|nurofen|vitamins?|moisturiser|tampons|pads)\b/i],
  ["pantry", /\b(rice|pasta|spaghetti|noodles|flour|sugar|salt|pepper|oil|olive oil|vinegar|sauce|stock|tinned|tin|can|canned|beans|lentils|chickpeas|oats|cereal|honey|jam|vegemite|peanut butter|spices?|paprika|cumin|cinnamon|baking|cocoa|chocolate|biscuits?|crackers|chips|nuts|tomato paste|passata|coconut milk|soy|curry paste|stock cubes?)\b/i],
];
/** Best guess at the shop section from the name ("beef mince" → Meat and seafood). */
export function guessSection(name: string): Section {
  const n = name.toLowerCase();
  if (/\bfrozen\b/.test(n)) return "frozen";
  for (const [s, re] of WORDS) if (re.test(n)) return s;
  return "other";
}

/** "500 g beef mince" → { qty: "500 g", item: "beef mince" }. */
export function splitIngredient(line: string): { qty: string | null; item: string } {
  const t = line.replace(/^[-*•]\s*/, "").trim();
  const m = t.match(/^((?:\d+\s+\d+\/\d+|\d+\s*[½¼¾⅓⅔⅛]|\d+(?:[.,/]\d+)?|[½¼¾⅓⅔⅛])(?:\s*(?:-|to)\s*(?:\d+(?:[./]\d+)?))?\s*(?:kg|g|grams?|mg|ml|millilitres?|l|litres?|liters?|tsp|teaspoons?|tbsp|tablespoons?|cups?|c\.|oz|lb|cloves?|cans?|tins?|pinch(?:es)?|handfuls?|bunch(?:es)?|slices?|sprigs?|sticks?|pieces?|packets?|packs?|loaf|loaves|bags?|bottles?|box(?:es)?|jars?|punnets?|dozen|heads?|x)?\.?)\s+(?:of\s+)?(.+)$/i);
  return m ? { qty: m[1].trim(), item: m[2].trim() } : { qty: null, item: t };
}

// ---------------------------------------------------------------------------
// Shopping list
// ---------------------------------------------------------------------------
export type ShopItem = { id: string; name: string; qty: string | null; section: Section; note: string | null; recipe_id: string | null; added_by: string | null; checked_at: string | null; created_at: string };

/** live: refresh every 8 s (the Kitchen list); off elsewhere (Home just needs a count). */
export function useShopping(live = true) {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["shopping", hid], enabled: !!hid, refetchInterval: live ? 8000 : false,
    queryFn: async () => {
      const { data, error } = await supabase.from("shopping_items").select("id,name,qty,section,note,recipe_id,added_by,checked_at,created_at")
        .eq("household_id", hid).is("deleted_at", null).order("created_at");
      if (error) throw error;
      return data as ShopItem[];
    },
  });
}

/** Things bought before that aren't on the list now, most bought first (one tap to add again). */
export function useRecentItems() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["shopping", hid, "recent"], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("shopping_items").select("name,section,deleted_at,checked_at").eq("household_id", hid).order("created_at", { ascending: false }).limit(500);
      if (error) throw error;
      const count = new Map<string, { name: string; section: Section; n: number }>();
      for (const r of data as { name: string; section: Section }[]) {
        const k = r.name.trim().toLowerCase(); const c = count.get(k);
        count.set(k, { name: c?.name ?? r.name.trim(), section: r.section, n: (c?.n ?? 0) + 1 });
      }
      return [...count.values()].sort((a, b) => b.n - a.n);
    },
  });
}

function useShopInv() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ["shopping", hid] });
}

export function useAddShopping() {
  const hid = useHouseholdId(); const me = useMe(); const inv = useShopInv();
  return useMutation({
    mutationFn: async (items: { name: string; qty?: string | null; section?: Section; recipe_id?: string | null }[]) => {
      const rows = items.filter((i) => i.name.trim()).map((i) => ({ household_id: hid, name: i.name.trim(), qty: i.qty?.trim() || null, section: i.section ?? guessSection(i.name), recipe_id: i.recipe_id ?? null, added_by: me?.id ?? null }));
      if (!rows.length) return;
      const { error } = await supabase.from("shopping_items").insert(rows);
      if (error) throw error;
    },
    onSuccess: inv,
  });
}

export function useUpdateShopping() {
  const me = useMe(); const inv = useShopInv(); const hid = useHouseholdId(); const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: { id: string; patch: Partial<Pick<ShopItem, "name" | "qty" | "section" | "note">> & { checked?: boolean; remove?: boolean } }) => {
      const { checked, remove, ...rest } = a.patch;
      const row: Record<string, unknown> = { ...rest };
      if (checked !== undefined) { row.checked_at = checked ? new Date().toISOString() : null; row.checked_by = checked ? me?.id ?? null : null; }
      if (remove) row.deleted_at = new Date().toISOString();
      const { error } = await supabase.from("shopping_items").update(row).eq("id", a.id);
      if (error) throw error;
    },
    // Tick straight away on screen; the database catches up.
    onMutate: async (a) => {
      await qc.cancelQueries({ queryKey: ["shopping", hid] });
      qc.setQueryData<ShopItem[]>(["shopping", hid], (old) => (old ?? []).flatMap((i) => i.id !== a.id ? [i] : a.patch.remove ? [] : [{ ...i, ...a.patch, checked_at: a.patch.checked === undefined ? i.checked_at : a.patch.checked ? new Date().toISOString() : null } as ShopItem]));
    },
    onSettled: inv,
  });
}

export function useClearTicked() {
  const hid = useHouseholdId(); const inv = useShopInv();
  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("shopping_items").update({ deleted_at: new Date().toISOString() }).eq("household_id", hid).is("deleted_at", null).not("checked_at", "is", null);
      if (error) throw error;
    },
    onSuccess: inv,
  });
}

// ---------------------------------------------------------------------------
// Recipe book
// ---------------------------------------------------------------------------
export type Ingredient = { qty: string | null; item: string; section?: Section };
export type Recipe = {
  id: string; title: string; source_kind: "link" | "photo" | "typed"; source_url: string | null; uploaded: boolean; mime: string | null;
  servings: number | null; prep_mins: number | null; cook_mins: number | null; ingredients: Ingredient[]; method: string[];
  notes: string | null; tags: string[]; favourite: boolean; ai_read_at: string | null; created_at: string;
};
export type RecipeDraft = Omit<Recipe, "id" | "uploaded" | "mime" | "ai_read_at" | "created_at"> & { id?: string };

export function useRecipes() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["recipes", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("recipes").select("id,title,source_kind,source_url,uploaded,mime,servings,prep_mins,cook_mins,ingredients,method,notes,tags,favourite,ai_read_at,created_at")
        .eq("household_id", hid).is("archived_at", null).order("title");
      if (error) throw error;
      return data as Recipe[];
    },
  });
}

function useRecipeInv() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return () => { void qc.invalidateQueries({ queryKey: ["recipes", hid] }); void qc.invalidateQueries({ queryKey: ["audit", hid] }); };
}

export function useSaveRecipe() {
  const hid = useHouseholdId(); const me = useMe(); const inv = useRecipeInv();
  return useMutation({
    mutationFn: async (r: RecipeDraft): Promise<string> => {
      const { id, ...row } = r;
      const clean = { ...row, title: row.title.trim(), ingredients: row.ingredients.filter((i) => i.item.trim()), method: row.method.map((m) => m.trim()).filter(Boolean) };
      if (id) {
        const { error } = await supabase.from("recipes").update(clean).eq("id", id);
        if (error) throw error;
        return id;
      }
      const { data, error } = await supabase.from("recipes").insert({ ...clean, household_id: hid, created_by: me?.id ?? null }).select("id").single();
      if (error) throw error;
      return (data as { id: string }).id;
    },
    onSuccess: inv,
  });
}

export function useArchiveRecipe() {
  const inv = useRecipeInv();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("recipes").update({ archived_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: inv,
  });
}

/** A photo or screenshot: a recipe row first (title "New recipe"), then the file to the Worker. */
export function useAddRecipePhoto() {
  const hid = useHouseholdId(); const me = useMe(); const inv = useRecipeInv();
  return useMutation({
    mutationFn: async (f: File): Promise<string> => {
      const id = crypto.randomUUID();
      const body = await shrink(f);
      const type = body.type || f.type || (f.name.toLowerCase().endsWith(".pdf") ? "application/pdf" : "");
      const { error } = await supabase.from("recipes").insert({ id, household_id: hid, title: "New recipe", source_kind: "photo", file_key: `${hid}/recipes/${id}`, file_name: f.name, created_by: me?.id ?? null });
      if (error) throw error;
      const r = await worker(`/recipes/${id}/file`, { method: "PUT", headers: { "Content-Type": type }, body });
      if (!r.ok) {
        const j = await r.json().catch(() => ({})) as { message?: string };
        await supabase.from("recipes").update({ archived_at: new Date().toISOString() }).eq("id", id);
        throw new Error(j.message ?? "Couldn't upload the photo.");
      }
      return id;
    },
    onSuccess: inv,
  });
}

export type ReadRecipe = { title: string; servings: number | null; prep_mins: number | null; cook_mins: number | null; ingredients: { qty: string | null; item: string }[]; method: string[]; notes: string | null };
export async function readRecipePhoto(id: string): Promise<ReadRecipe> {
  const r = await worker(`/recipes/${id}/read`, { method: "POST" });
  const j = (await r.json().catch(() => ({}))) as { suggested?: ReadRecipe; message?: string };
  if (!r.ok || !j.suggested) throw new Error(j.message ?? "The reader couldn't read it.");
  return j.suggested;
}

export type Imported = { title: string; servings: number | null; prep_mins: number | null; cook_mins: number | null; ingredients: string[]; method: string[]; notes: string | null; source_url: string };
export async function importRecipe(url: string): Promise<Imported> {
  const r = await worker("/recipes/import", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ url }) });
  const j = (await r.json().catch(() => ({}))) as { recipe?: Imported; message?: string };
  if (!r.ok || !j.recipe) throw new Error(j.message ?? "Couldn't read that page.");
  return j.recipe;
}

// ---------------------------------------------------------------------------
// Markdown in and out
// ---------------------------------------------------------------------------
export const MD_TEMPLATE = `# Recipe name

Serves: 4
Prep: 15 min
Cook: 30 min

## Ingredients
- 500 g beef mince
- 1 onion, diced
- 2 cloves garlic

## Method
1. Brown the mince.
2. Add the onion and garlic.

## Notes
Any tips here.
`;

/** Read a Markdown recipe (e.g. one written with Claude): # title, Serves/Prep/Cook lines,
 *  ## Ingredients (- items), ## Method (1. steps), anything else into notes. */
export function parseMarkdown(md: string): Omit<RecipeDraft, "source_kind" | "source_url" | "tags" | "favourite"> {
  let title = ""; let servings: number | null = null; let prep: number | null = null; let cook: number | null = null;
  const ingredients: Ingredient[] = []; const method: string[] = []; const notes: string[] = [];
  let part: "top" | "ing" | "method" | "notes" = "top";
  const mins = (s: string) => { const h = s.match(/(\d+)\s*h/i); const m = s.match(/(\d+)\s*m/i); const n = s.match(/^\D*(\d+)\D*$/); return h || m ? Number(h?.[1] ?? 0) * 60 + Number(m?.[1] ?? 0) : n ? Number(n[1]) : null; };
  for (const raw of md.split(/\r?\n/)) {
    const line = raw.trim(); if (!line) continue;
    const head = line.match(/^#{1,6}\s+(.*)$/);
    if (head) {
      const t = head[1].trim(); const l = t.toLowerCase();
      if (!title && line.startsWith("# ")) { title = t; continue; }
      part = /ingredient/.test(l) ? "ing" : /method|steps|directions|instructions/.test(l) ? "method" : "notes";
      if (part === "notes" && !/notes?|tips?/.test(l)) notes.push(t);
      continue;
    }
    const kv = line.match(/^\*{0,2}(serves|servings|makes|prep(?: time)?|cook(?:ing)?(?: time)?)\*{0,2}\s*:\s*(.+)$/i);
    if (kv && part !== "method") {
      const k = kv[1].toLowerCase();
      if (/serv|makes/.test(k)) servings = Number(kv[2].match(/\d+/)?.[0] ?? NaN) || null;
      else if (/prep/.test(k)) prep = mins(kv[2]);
      else cook = mins(kv[2]);
      continue;
    }
    if (part === "ing") { const s = splitIngredient(line); ingredients.push({ ...s, section: guessSection(s.item) }); }
    else if (part === "method") method.push(line.replace(/^(\d+[.)]|[-*•])\s*/, ""));
    else if (part === "notes" || title) notes.push(line);
  }
  return { title: title || "New recipe", servings, prep_mins: prep, cook_mins: cook, ingredients, method, notes: notes.join("\n") || null };
}

export function toMarkdown(r: Pick<Recipe, "title" | "servings" | "prep_mins" | "cook_mins" | "ingredients" | "method" | "notes" | "source_url">): string {
  const out = [`# ${r.title}`, ""];
  if (r.servings) out.push(`Serves: ${r.servings}`);
  if (r.prep_mins) out.push(`Prep: ${r.prep_mins} min`);
  if (r.cook_mins) out.push(`Cook: ${r.cook_mins} min`);
  if (r.source_url) out.push(`Source: ${r.source_url}`);
  out.push("", "## Ingredients", ...r.ingredients.map((i) => `- ${i.qty ? `${i.qty} ` : ""}${i.item}`), "", "## Method", ...r.method.map((m, i) => `${i + 1}. ${m}`));
  if (r.notes) out.push("", "## Notes", r.notes);
  return out.join("\n") + "\n";
}

// ---------------------------------------------------------------------------
// Recipe book tabs (028). A recipe is under a tab when the tab's name is one of
// its tags, so one recipe can sit under several tabs.
// ---------------------------------------------------------------------------
export type TabColour = "chart-1" | "chart-2" | "chart-3" | "chart-4" | "chart-5" | "chart-6";
export const TAB_COLOURS: TabColour[] = ["chart-1", "chart-2", "chart-3", "chart-4", "chart-5", "chart-6"];
export type RecipeTab = { id: string; name: string; colour: TabColour; sort_order: number };
export const hasTab = (r: Pick<Recipe, "tags">, t: Pick<RecipeTab, "name">) => r.tags.some((g) => g.trim().toLowerCase() === t.name.trim().toLowerCase());

export function useRecipeTabs() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["recipe_tabs", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("recipe_tabs").select("id,name,colour,sort_order").eq("household_id", hid).is("archived_at", null).order("sort_order").order("name");
      if (error) throw error;
      return data as RecipeTab[];
    },
  });
}

/** Add or change a tab. A rename carries the recipes with it (their tag is renamed too). */
export function useSaveTab() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: { tab: Partial<RecipeTab> & { name: string; colour: TabColour }; oldName?: string; recipes?: Recipe[] }) => {
      const name = a.tab.name.trim();
      if (a.tab.id) {
        const { error } = await supabase.from("recipe_tabs").update({ name, colour: a.tab.colour }).eq("id", a.tab.id);
        if (error) throw error;
        const old = a.oldName?.trim().toLowerCase();
        if (old && old !== name.toLowerCase()) {
          for (const r of (a.recipes ?? []).filter((x) => x.tags.some((g) => g.trim().toLowerCase() === old))) {
            const tags = r.tags.map((g) => (g.trim().toLowerCase() === old ? name : g));
            const { error: e2 } = await supabase.from("recipes").update({ tags }).eq("id", r.id);
            if (e2) throw e2;
          }
        }
      } else {
        const { error } = await supabase.from("recipe_tabs").insert({ household_id: hid, name, colour: a.tab.colour, sort_order: a.tab.sort_order ?? 100 });
        if (error) throw new Error(error.code === "23505" ? "There's already a tab with that name." : error.message);
      }
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["recipe_tabs", hid] }); void qc.invalidateQueries({ queryKey: ["recipes", hid] }); },
  });
}

/** Take a tab out of the book. Recipes keep their tag, so nothing is lost. */
export function useArchiveTab() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("recipe_tabs").update({ archived_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["recipe_tabs", hid] }),
  });
}

// ---------------------------------------------------------------------------
// Meal plan (031). James 10 Oct: "love the weekly meal plan, put that in for sure."
// ---------------------------------------------------------------------------
export type Slot = "breakfast" | "lunch" | "dinner" | "snack";
export const SLOTS: { key: Slot; label: string }[] = [{ key: "breakfast", label: "Breakfast" }, { key: "lunch", label: "Lunch" }, { key: "dinner", label: "Dinner" }, { key: "snack", label: "Snack" }];
export type Meal = { id: string; plan_on: string; slot: Slot; recipe_id: string | null; label: string | null; servings: number | null; note: string | null; added_to_list_at: string | null };

export function useMeals(from: string, to: string) {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["meals", hid, from, to], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("meal_plan_items").select("id,plan_on,slot,recipe_id,label,servings,note,added_to_list_at")
        .eq("household_id", hid).is("archived_at", null).gte("plan_on", from).lte("plan_on", to).order("plan_on");
      if (error) throw error;
      return data as Meal[];
    },
  });
}

export function useSaveMeal() {
  const hid = useHouseholdId(); const me = useMe(); const qc = useQueryClient();
  return useMutation({
    mutationFn: async (m: Partial<Meal> & { plan_on: string; remove?: boolean }) => {
      const { id, remove, ...row } = m;
      if (id) {
        const { error } = await supabase.from("meal_plan_items").update(remove ? { archived_at: new Date().toISOString() } : row).eq("id", id);
        if (error) throw error; return;
      }
      const { error } = await supabase.from("meal_plan_items").insert({ ...row, household_id: hid, added_by: me?.id ?? null });
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["meals", hid] }),
  });
}

export function useMarkMealsListed() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      if (!ids.length) return;
      const { error } = await supabase.from("meal_plan_items").update({ added_to_list_at: new Date().toISOString() }).in("id", ids);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["meals", hid] }),
  });
}

// Scaling amounts ("1 ½ cups" × 2 → "3 cups"), shared by the recipe page and the meal plan.
export const FRAC: Record<string, number> = { "½": 0.5, "¼": 0.25, "¾": 0.75, "⅓": 1 / 3, "⅔": 2 / 3, "⅛": 0.125 };
export function niceNum(n: number) {
  const whole = Math.floor(n + 1e-9); const rest = n - whole;
  const f = ([[0, ""], [0.125, "⅛"], [0.25, "¼"], [1 / 3, "⅓"], [0.5, "½"], [2 / 3, "⅔"], [0.75, "¾"], [1, ""]] as [number, string][]).reduce((b, x) => (Math.abs(x[0] - rest) < Math.abs(b[0] - rest) ? x : b));
  if (n >= 10 || Math.abs(f[0] - rest) > 0.04) return (Math.round(n * 10) / 10).toString();
  const w = f[0] === 1 ? whole + 1 : whole;
  return f[1] ? (w ? `${w} ${f[1]}` : f[1]) : String(w);
}
/** "1 1/2 cups" × 2 → "3 cups". Leaves amounts it can't read alone. */
export function scaleQty(q: string | null, by: number): string | null {
  if (!q || by === 1) return q;
  const m = q.match(/^(\d+\s+\d+\/\d+|\d+\s*[½¼¾⅓⅔⅛]|\d+\/\d+|\d+(?:[.,]\d+)?|[½¼¾⅓⅔⅛])(\s*(?:-|to)\s*(\d+(?:[.,]\d+)?))?(.*)$/);
  if (!m) return q;
  const val = (s: string) => { if (FRAC[s] !== undefined) return FRAC[s]; const uni = s.match(/^(\d+)\s*([½¼¾⅓⅔⅛])$/); if (uni) return +uni[1] + FRAC[uni[2]]; const mixed = s.match(/^(\d+)\s+(\d+)\/(\d+)$/); if (mixed) return +mixed[1] + +mixed[2] / +mixed[3]; const fr = s.match(/^(\d+)\/(\d+)$/); if (fr) return +fr[1] / +fr[2]; return Number(s.replace(",", ".")); };
  return `${niceNum(val(m[1]) * by)}${m[3] ? ` to ${niceNum(val(m[3]) * by)}` : ""}${m[4]}`;
}

